import { Box, Grid } from '@mui/material';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import uniqid from 'uniqid';
import { Product } from '../../apiTypes';
import EditModal from '../../components/EditModal';
import FormButtonSelect from '../../components/forms/FomsButtonSelect';
import FormAutocomplete from '../../components/forms/FormAutocomplete';
import FormTextField from '../../components/forms/FormTextField';
import FormUploadImg from '../../components/FormUploadImg';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useCafe } from '../../contexts/cafe.context';
import { useLocale } from '../../contexts/locale.context';
import { deleteDoc, setDoc, updateDoc } from '../../utils/api';

type Props = {
  open: boolean;
  product?: DbDocument<Product> | null;
  onClose: () => void;
  onSubmit?: (product: Product & { id: string }) => void;
};

const EditProduct = ({ product, open, onClose, onSubmit }: Props) => {
  const { cafeSlug } = useAdmin();
  const { lang } = useLocale();
  const { products } = useCafe();

  const form = useForm<Product>({
    mode: 'onChange',
    defaultValues: {
      name: product?.name || '',
      provider: product?.provider || '',
      category: product?.category || '',
      unit: product?.unit || 'pc',
      imageUrl: product?.imageUrl || '',
      qpp: product?.qpp || undefined,
      ean: product?.ean || '',
    },
  });

  const categories = useMemo(() => Array.from(new Set(products.map((c) => c.category))).filter((c) => !!c), [products]);

  const providers = useMemo(() => Array.from(new Set(products.map((c) => c.provider))).filter((p) => !!p), [products]);

  const handleClose = () => {
    onClose();
    form.reset();
  };

  const saveChanges = (newProduct: Product) => {
    if (product?.id) {
      updateDoc<Product>(`cafes/${cafeSlug}/products/${product.id}`, newProduct);
      onSubmit && onSubmit({ id: product.id, ...newProduct });
    } else {
      const id = uniqid();
      setDoc<Product>(`cafes/${cafeSlug}/products/${uniqid()}`, newProduct);
      onSubmit && onSubmit({ id, ...newProduct });
    }
    handleClose();
  };

  const removeProduct = async () => {
    if (product?.id) {
      // stores.forEach((store) => {
      //   updateDoc(doc(db, "company", companySlug, "stores", store.id), {
      //     [`residues.${consumable.id}`]: deleteField(),
      //   });
      // });
      deleteDoc(`cafes/${cafeSlug}/products/${product.id}`);
      handleClose();
    }
  };

  return (
    <EditModal<Product>
      title={t.product[lang]}
      open={open}
      onClose={handleClose}
      submitButton={product ? t.save_changes[lang] : t.add[lang]}
      onSubmit={saveChanges}
      secondaryButton={product ? t.remove[lang] : undefined}
      confirmText={t.remove_product_confirm[lang]}
      onSecondaryButtonClick={removeProduct}
      form={form}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <Box sx={{ mr: 3, flexShrink: 0 }}>
          <FormUploadImg name="imageUrl" defaultValue={product?.imageUrl || ''} folder="products" />
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={12}>
            <FormTextField name="name" label={t.name[lang]} rules={{ required: true }} fullWidth />
          </Grid>

          <Grid item xs={12} sm={12}>
            <FormAutocomplete name="category" label={t.category[lang]} options={categories} />
          </Grid>

          <Grid item xs={12} sm={12}>
            <FormButtonSelect name="unit" label={t.unit[lang]} options={['pc', 'g', 'ml']} />
          </Grid>

          <Grid item xs={12} sm={12}>
            <FormTextField
              name="qpp"
              type="number"
              label={t.quantity_per_package[lang]}
              rules={{ min: 1, pattern: /^[0-9]{0,6}$/ }}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={12} sx={{ mt: 3 }}>
            <FormAutocomplete name="provider" label={t.provider[lang]} options={providers} />
          </Grid>

          <Grid item xs={12} sm={12}>
            <FormTextField name="ean" label={t.article[lang]} defaultValue={product?.ean || ''} fullWidth />
          </Grid>
        </Grid>
      </Box>
    </EditModal>
  );
};

export default EditProduct;
